import { useCallback, useEffect, useState, useRef } from 'react'
import { useEditorStore } from '../store/editorStore'
import { useShallow } from 'zustand/react/shallow'

export function useCanvasControls(
  containerRef: React.RefObject<HTMLDivElement>
) {
  const {
    stageX, stageY, stageScale,
    setStageTransform, setStageSize,
    clearSelection,
    setTool,
    undo, redo,
    setDrawingTrayFromId,
  } = useEditorStore(useShallow(state => ({
    stageX: state.stageX, stageY: state.stageY, stageScale: state.stageScale,
    setStageTransform: state.setStageTransform, setStageSize: state.setStageSize,
    clearSelection: state.clearSelection, setTool: state.setTool,
    undo: state.undo, redo: state.redo, setDrawingTrayFromId: state.setDrawingTrayFromId
  })))

  const canUndo = useEditorStore(s => s._historyIdx > 0)
  const canRedo = useEditorStore(s => s._historyIdx < s._history.length - 1)

  const [ghostPos, setGhostPos] = useState<{ x: number; y: number } | null>(null)
  const marqueeStart = useRef<{ x: number; y: number } | null>(null)
  const isDraggingMarquee = useRef(false)

  // ── Resize Observer ──────────────────────────────────────
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const obs = new ResizeObserver(entries => {
      for (const e of entries) {
        setStageSize(e.contentRect.width, e.contentRect.height)
      }
    })
    obs.observe(el)
    setStageSize(el.clientWidth, el.clientHeight)
    return () => obs.disconnect()
  }, [setStageSize, containerRef])

  // ── Keyboard shortcuts ───────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return

      // Ctrl+Z / Ctrl+Y — undo / redo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault()
        if (canUndo) undo()
        return
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault()
        if (canRedo) redo()
        return
      }

      // Ctrl+A — select all
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault()
        useEditorStore.getState().selectAll()
        return
      }

      // Ctrl+D — duplicate selected
      if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
        e.preventDefault()
        useEditorStore.getState().duplicateSelected()
        return
      }

      // Ctrl+C — copy selected
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        e.preventDefault()
        useEditorStore.getState().copySelected()
        return
      }

      // Ctrl+V — paste clipboard
      if ((e.ctrlKey || e.metaKey) && e.key === 'v' && !e.shiftKey) {
        e.preventDefault()
        useEditorStore.getState().pasteClipboard()
        return
      }

      // Escape
      if (e.key === 'Escape') {
        setDrawingTrayFromId(null)
        setGhostPos(null)
        clearSelection()
        setTool('select')
        return
      }

      // Tools & Generators
      if (!e.ctrlKey && !e.metaKey && !e.altKey) {
        if (e.key === 'v' || e.key === 'V') setTool('select')
        if (e.key === 'd' || e.key === 'D') setTool('draw_tray')
        if (e.key === 'e' || e.key === 'E') setTool('erase')
        if (e.key === 'r' || e.key === 'R') useEditorStore.getState().setShowRowGenerator(true)
        if (e.key === 'w' || e.key === 'W') useEditorStore.getState().setShowSetupWizard(true)
        if (e.key === 'm' || e.key === 'M') useEditorStore.getState().setShowRackAllocation(true)
        if (e.key === 'p' || e.key === 'P') useEditorStore.getState().setShowPathwayGenerator(true)
      }

      // Delete / Backspace
      if (e.key === 'Delete' || e.key === 'Backspace') {
        useEditorStore.getState().deleteSelected()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [canUndo, canRedo, undo, redo, clearSelection, setTool, setDrawingTrayFromId])

  // ── Zoom (wheel) ─────────────────────────────────────────
  const handleWheel = useCallback((e: any) => {
    e.evt.preventDefault()
    const stage = e.target.getStage()
    const oldScale = stage.scaleX()
    const ptr = stage.getPointerPosition()
    const scaleFactor = e.evt.deltaY < 0 ? 1.12 : 1 / 1.12
    const newScale = Math.max(0.08, Math.min(8, oldScale * scaleFactor))
    const mousePointTo = {
      x: (ptr.x - stage.x()) / oldScale,
      y: (ptr.y - stage.y()) / oldScale,
    }
    setStageTransform(
      ptr.x - mousePointTo.x * newScale,
      ptr.y - mousePointTo.y * newScale,
      newScale
    )
  }, [setStageTransform])

  // ── Pan (stage drag) ─────────────────────────────────────
  const handleStageDragEnd = useCallback((e: any) => {
    if (e.target !== e.target.getStage()) return
    setStageTransform(e.target.x(), e.target.y(), stageScale)
  }, [setStageTransform, stageScale])

  const screenToWorld = useCallback((sx: number, sy: number) => ({
    x: (sx - stageX) / stageScale,
    y: (sy - stageY) / stageScale,
  }), [stageX, stageY, stageScale])

  return {
    handleWheel,
    handleStageDragEnd,
    screenToWorld,
    ghostPos,
    setGhostPos,
    marqueeStart,
    isDraggingMarquee,
  }
}
